import En from '@/common/classes/En'
import { CLIENT_IMPORT_RUNTIME_WINDOW_KEYS } from './runtime-window-globals'

type ClientImportRegistration = [string, any]

type ClientImportRuntimeParams = {
  currentFormId: string | number | undefined
  currentPanelId: string | number | undefined
  pageData: any
  baseFields: Record<string, any>
  registrations: ClientImportRegistration[]
  elements: Record<string, any>
  actions: Record<string, any>
  getForm: (...args: any[]) => any
  verifyForm?: (formId: string) => boolean
  runActionByName: (...args: any[]) => any
  runFormEvent?: (ctx: any) => any
  invokeBusEvent?: (eventName: string, data?: any) => any
  reloadElement: (...args: any[]) => any
  runAction: (ctx: any) => any
  empty?: (elementName: string) => void
}

const captureWindowProperties = (keys: string[]) => {
  return new Map(
    keys.map((key) => [key, Object.getOwnPropertyDescriptor(window, key)])
  )
}

const restoreWindowProperties = (snapshot: Map<string, PropertyDescriptor | undefined>) => {
  snapshot.forEach((descriptor, key) => {
    if (descriptor) {
      Object.defineProperty(window, key, descriptor)
      return
    }

    delete (window as any)[key]
  })
}

export const createClientImportRuntime = ({
  currentFormId,
  currentPanelId,
  pageData,
  baseFields,
  registrations,
  elements,
  actions,
  getForm,
  verifyForm,
  runActionByName,
  runFormEvent,
  invokeBusEvent,
  reloadElement,
  runAction,
  empty,
}: ClientImportRuntimeParams) => {
  const runtimeWindowKeys = [
    ...CLIENT_IMPORT_RUNTIME_WINDOW_KEYS,
    ...registrations.map(([name]) => name),
  ]
  const snapshot = captureWindowProperties(runtimeWindowKeys)

  const enContext = new En({
    controls: {
      getForm,
      verifyForm,
      setValueToTempData: (_value: any) => { /* TODO: */ },
      runActionByName,
      runFormEvent,
      invokeBusEvent,
      reloadElement,
      empty,
      runElementEvent: (ctx: {
        elementId: number | string
        eventName: string
        data?: any
        formId?: string
        panelId?: string
        name?: string
      }) => {
        const isUniDataProxy = ctx.data &&
          typeof (ctx.data as any).getField === 'function' &&
          typeof (ctx.data as any).hasField === 'function'
        const selectedData = isUniDataProxy
          ? (typeof window !== 'undefined' ? (window as any).selected : undefined)
          : (ctx.data?.selectedData ?? ctx.data?.selected ?? ctx.data)
        const fallbackCtx = {
          level: 'element' as const,
          eventName: ctx.eventName,
          formId: String(ctx.formId ?? ''),
          panelId: String(ctx.panelId ?? ''),
          elementId: String(ctx.elementId),
          data: { _attributes: { id: ctx.elementId }, name: { _text: ctx.name ?? '' } },
          selectedData,
        }
        runAction(fallbackCtx)
      },
    },
    elements,
    getElements: () => elements,
    actions,
    pageData: pageData || {},
    formId: currentFormId ? parseInt(String(currentFormId)) : undefined,
    panelId: currentPanelId ? parseInt(String(currentPanelId)) : undefined,
  })

  registrations.forEach(([name, item]) => {
    enContext.registerClientFunction(name, item.func)
  })

  enContext._resetData()
  enContext._addData(baseFields)
  enContext._setFormId(currentFormId)
  enContext._setPanelId(currentPanelId)
  enContext._setPageData(pageData || {})
  ;(enContext as any)._setupGlobalScope()

  return {
    enContext,
    restore: () => restoreWindowProperties(snapshot),
  }
}
